/**
 * RP4 — Scene mesh factory functions extracted from `Scene3DStage.tsx`.
 *
 * Pure THREE.js mesh builders for non-line, non-text entity kinds:
 * hatch fills, wipeout masks, instanced array-rect bases.
 *
 * # Why a dedicated module
 *
 * These factories are stateless — they take a `RenderableEntity` plus
 * (optionally) the live camera zoom and produce a fresh THREE.Object3D.
 * Hoisting them out of the orchestrator keeps `Scene3DStage.tsx` close
 * to the orchestrator-only budget and lets the bulk-load effect import
 * a single named factory per renderable kind without dragging the rest
 * of the file's closures along.
 *
 * Text builders live in `./sceneTextMeshFactories.ts` to keep both
 * files under the 400 LOC cap.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import * as THREE from "three";

import type { RenderableEntity } from "../types";
import { directMeshLayerStamp } from "../layerChainVisibility";
import {
  hatchLoopsToTessellationInput,
  tessellateHatchLoops,
} from "../hatch/earcutLoops";
import {
  getOrCreatePatternTexture,
  getOrCreateGradientTexture,
} from "../hatch/hatchPatterns";
import { shouldUseSolidFallback, bboxAreaWorld } from "../hatch/hatchScreenGate";
import { fillModePaintsSolid } from "../adapters/geom/polylineDrawnBand";
import { tessellationToBufferGeometry } from "./fillMeshGeometry";
import {
  computeArrayInstanceTransforms,
  computeArrayLevelOffsetVectors,
} from "../adapters/arrayRectToInstanced";

/**
 * PR-D + Q12.B — build `THREE.InstancedMesh` instances for an
 * `array-rect` renderable. One InstancedMesh per resolved base
 * entity, with `rows × cols × levels` translation matrices (W1.5 adds
 * the Z-level stack; a plain 2D array has `levels === 1`).
 *
 * # Base resolution
 *
 * `array-rect.baseEntityIds` lists the source entities. We look up
 * each in the per-frame `renderableById` map. If a base is missing
 * (e.g. the source block hasn't been flattened yet — PR-F territory)
 * we skip that base; the array still renders the rest.
 *
 * # Geometry strategy
 *
 *   - Solid hatch base → reuses the hatch fill geometry as a
 *     `THREE.InstancedMesh` (true Three.js instancing).
 *   - Line base → produces a `THREE.Group` containing one translated
 *     `THREE.Mesh` (instance count clones). Falls back to a small
 *     placeholder marker if the base has no buildable mesh — array
 *     becomes a grid of dots until the block flatten lands in PR-F.
 *
 * # Origin offset
 *
 * Already applied at adapter level (`arrayRectToInstanced`); per-cell
 * translation is in offset-world space.
 *
 * # Base / axis wiring + Z-Levels (W1.5)
 *
 * `basePoint` / `axisAngleDeg` are now read from the resolved geometry
 * (previously hardcoded to the origin / 0°, Codex P2 #502 findings 2+3
 * — a Base X/Y or Axis angle property edit moved the bounds + grips but
 * never the on-screen mesh). `levels` / `levelSpacing` add a 3-axis
 * Z-stack: AutoCAD's ARRAYRECT repeats the whole rows×cols grid
 * `levels` times, each copy offset by `levelIndex * levelSpacing` along
 * Z — every level reuses the SAME XY transforms
 * (`computeArrayInstanceTransforms`), so the instance count becomes
 * `rows * cols * levels`. `basePoint.z` is the array's base ELEVATION
 * (S-ARRAYRECT-BASEPOINT-Z) — every instance's Z is `basePoint.z +
 * levelIndex * levelSpacing`, so level 0 sits AT the base elevation
 * instead of an implicit world Z=0.
 */
export function buildArrayInstancedMeshes(
  renderable: RenderableEntity,
  renderableById: Map<string, RenderableEntity>,
): THREE.Object3D[] {
  if (renderable.geometry.kind !== "array-rect") return [];
  const {
    baseEntityIds,
    rows,
    cols,
    rowSpacing,
    colSpacing,
    basePoint,
    axisAngleDeg,
    levels,
    levelSpacing,
    basePlaneNormal,
  } = renderable.geometry;
  // S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 8) —
  // `basePlaneNormal` threaded through so the row/col grid ITSELF stays IN
  // the array's own base plane for a TILTED associative array (round 6/7
  // stacked levels along the tilted normal but left the cell grid in flat
  // WCS XY, so cells and levels disagreed about which axis was in-plane —
  // e.g. a normal along WCS +X made columns run parallel to the level-
  // stacking direction instead of staying in-plane). `t.tz` carries the
  // cell's own Z-lift from the tilt (0 for the common flat array).
  const xyTransforms = computeArrayInstanceTransforms({
    basePoint,
    rows,
    cols,
    rowSpacing,
    colSpacing,
    axisAngleDeg,
    basePlaneNormal,
  });
  // S-ARRAYRECT-BASEPLANE-NORMAL (Codex P2, local review round 6) — full 3-D
  // level offset VECTORS (stacks along the array's own base-plane normal for
  // a TILTED associative array; reduces to the pre-existing pure-Z stack for
  // the common flat array, `basePlaneNormal` undefined).
  const levelOffsets = computeArrayLevelOffsetVectors(levels, levelSpacing, basePlaneNormal);
  const out: THREE.Object3D[] = [];

  // No resolved bases → emit a single placeholder InstancedMesh of
  // 4×4 px dots so the user sees the grid (mirrors SVG fallback's
  // crosshair markers per `renderArray`).
  const baseRenderables: RenderableEntity[] = [];
  for (const id of baseEntityIds) {
    const r = renderableById.get(id);
    if (r) baseRenderables.push(r);
  }
  const placeholderGeometry = new THREE.PlaneGeometry(2, 2);
  const placeholderMaterial = new THREE.MeshBasicMaterial({
    color: new THREE.Color(renderable.colorHex).getHex(),
    side: THREE.DoubleSide,
  });
  const baseGeometries: { geometry: THREE.BufferGeometry; material: THREE.Material }[] = [];
  for (const base of baseRenderables) {
    if (base.geometry.kind === "hatch") {
      const mesh = buildHatchFillMesh(base);
      if (mesh) {
        baseGeometries.push({ geometry: mesh.geometry, material: mesh.material as THREE.Material });
      }
    } else if (base.geometry.kind === "wipeout") {
      const mesh = buildWipeoutFillMesh(base);
      if (mesh) {
        baseGeometries.push({ geometry: mesh.geometry, material: mesh.material as THREE.Material });
      }
    }
    // Other base kinds (line / polyline / circle) need block-flatten
    // support — landed in PR-F.  The placeholder InstancedMesh below
    // covers them in the meantime.
  }

  if (baseGeometries.length === 0) {
    baseGeometries.push({
      geometry: placeholderGeometry,
      material: placeholderMaterial,
    });
  } else {
    placeholderGeometry.dispose();
    placeholderMaterial.dispose();
  }

  const instanceCount = xyTransforms.length * levelOffsets.length;
  for (const bg of baseGeometries) {
    const instanced = new THREE.InstancedMesh(
      bg.geometry,
      bg.material,
      instanceCount,
    );
    instanced.userData = {
      entityId: renderable.id,
      kind: "array-rect",
      // Own layer (+ block-reference chain) stamp — uniform with every other
      // direct mesh (`isDirectMeshOnFrozenLayer`, Codex PR #784 P1 follow-up).
      ...directMeshLayerStamp(renderable),
    };
    const m = new THREE.Matrix4();
    let i = 0;
    for (const levelOffset of levelOffsets) {
      for (const t of xyTransforms) {
        // basePoint.z is the array's base ELEVATION — level 0 (offset={0,0,0})
        // sits AT that elevation, not implicitly at world Z=0 (Codex P2,
        // found in review: an elevated 3-axis array rendered every level
        // shifted downward by its own base Z). `levelOffset` is a full 3-D
        // vector (Codex P2 round 6) — for the common flat array it is
        // exactly `{x:0, y:0, z: levelIndex * levelSpacing}`. `t.tz` (round
        // 8) is the CELL's own Z-lift from a tilted base plane — both are 0
        // for the common flat array, so this stays byte-identical to the
        // pre-existing pure-Z stacking; for a tilted associative array the
        // cell grid AND the level stack now agree on the same in-plane axes.
        m.makeTranslation(
          t.tx + levelOffset.x,
          t.ty + levelOffset.y,
          basePoint.z + t.tz + levelOffset.z,
        );
        instanced.setMatrixAt(i, m);
        i++;
      }
    }
    instanced.instanceMatrix.needsUpdate = true;
    instanced.frustumCulled = false;
    out.push(instanced);
  }

  return out;
}

/**
 * PR-D — build a `THREE.Mesh` for a HATCH renderable. Solid hatches
 * use `MeshBasicMaterial` with the resolved fill color; pattern
 * hatches sample the cached canvas texture with `RepeatWrapping`.
 *
 * Returns `null` for hatches with empty / degenerate boundary loops.
 *
 * PR-V5 — when `cameraZoom` is supplied AND a PATTERN hatch covers
 * less than 4×4 px on screen, the pattern texture path is bypassed and
 * a flat-coloured solid mesh is emitted instead. This mirrors AutoCAD's
 * HPMAXLINES pattern line-count safeguard at extreme zoom-out (the
 * pattern visibly disappears below the unit-step). See
 * `hatchScreenGate.ts` for the threshold. Omitting `cameraZoom`
 * disables the gate entirely (= full-fidelity pattern rendering, the
 * pre-V5 path).
 *
 * The gate is PATTERN-only. A GRADIENT hatch is NEVER screen-gated: a
 * gradient is a solid-class fill with zero pattern lines (ezdxf: "a
 * gradient filled hatch is also a solid filled hatch"), so the
 * line-count safeguard is structurally inapplicable and no CAD collapses
 * a gradient ramp to one colour by screen size. A gradient ALWAYS renders
 * its full ramp regardless of `cameraZoom` / screen area (AutoCAD parity,
 * CLAUDE.md ПРИОРИТЕТ #1 — no representative-colour substitution). The
 * only gradient flat fallback is the degenerate-bbox NaN guard inside
 * `buildGradientMaterial`, which is zoom-independent.
 *
 * # `$FILLMODE` 0 — no fill of ANY tier, and the boundary carries the entity
 *
 * Autodesk, "To turn on or off the display of solid fills": "When you turn off
 * Fill mode, wide polylines, solid-filled polygons, gradient fill, and hatches
 * are displayed in outline form"; FILL (Command), Off: "Only the outlines of
 * objects are displayed and plotted." The list is unqualified — gradient fill
 * is named explicitly and `hatches` carries no "solid" qualifier — so ALL
 * THREE tiers this factory can emit (solid, gradient, pattern) are suppressed
 * together. `ezdxf` renders the same state through
 * `HatchPolicy.SHOW_OUTLINE` ("show only the outline of HATCH entities"):
 * `addons/drawing/frontend.py::draw_hatch_entity` replaces `filling` with a
 * plain `Filling()` BEFORE the `filling.type == Filling.PATTERN` branch, so a
 * patterned hatch never reaches `draw_hatch_pattern` either, and the boundary
 * paths are then handed to `draw_path` (STROKED) rather than to
 * `draw_filled_paths`.
 *
 * The gate is therefore the FIRST thing this function does — ahead of the
 * boundary walk, the screen gate, earcut and every texture bake — and it reads
 * `RenderableEntity.fillmode` through {@link fillModePaintsSolid} — the ONE
 * predicate, shared with `batchedLayer.ts`'s outline stream and with the PLOT
 * (`print/pdf/converters/hatchConverter.ts`), and the same one the wide-polyline
 * band, its draw-order plan, the pointer pick and window select have read since
 * @codex round 11/12 (this hatch read is round 16, P2). Absent means 1, so a
 * default drawing is byte-unchanged.
 *
 * The outline half of the zero state is NOT this module's: a hatch has no fill
 * mesh to stroke at 0, so the boundary loops are emitted into the gl.LINES
 * buckets by `batchedLayer.ts::hatchOutlineLineVertices` — where they pick up
 * the entity's colour, lineweight, linetype AND its per-instance selection
 * byte. Returning `null` here without that stream would make an outlined hatch
 * INVISIBLE, which is a worse bug than the one this closes.
 */
export function buildHatchFillMesh(
  renderable: RenderableEntity,
  cameraZoom?: number,
): THREE.Mesh | null {
  if (renderable.geometry.kind !== "hatch") return null;
  // `$FILLMODE` 0 — no solid, no gradient, no pattern. See the header.
  if (!fillModePaintsSolid(renderable.fillmode)) return null;
  const {
    loops,
    patternName,
    patternScale,
    patternAngle,
    solid,
    gradient,
    hadTaggedExterior,
  } = renderable.geometry;
  if (loops.length === 0) return null;

  // PR-V5 — screen-area gate. Compute the upper-bound bbox area
  // (overestimate is OK — better full-fidelity than a borderline
  // skip) and decide whether the pattern path is worth paying for.
  // Solid hatches don't have a pattern texture to skip, so the gate
  // only redirects pattern hatches; solid hatches stay solid.
  //
  // The gate is PATTERN-only. A gradient hatch is NEVER screen-gated
  // (AutoCAD parity, CLAUDE.md ПРИОРИТЕТ #1 — no representative-colour
  // substitution): AutoCAD's "tiny hatch → solid" path is the HPMAXLINES
  // pattern line-count safeguard, which is structurally inapplicable to a
  // gradient — a gradient-filled hatch is a SOLID-class fill with ZERO
  // pattern lines (ezdxf: "a gradient filled hatch is also a solid filled
  // hatch"), so there is no line count to cap. No CAD collapses a gradient
  // ramp to one colour by screen size; the only thing that suppresses a
  // gradient is the global $FILLMODE outline toggle, and that has already run
  // at the top of this function (Autodesk names `gradient fill` in the
  // outline-form list outright). So a gradient ALWAYS renders its ramp
  // regardless of cameraZoom / screen area. (The only flat fallback for a
  // gradient is the degenerate-bbox NaN guard inside buildGradientMaterial —
  // a zoom-independent zero-area guard, not a gate.)
  const hasGradient = gradient !== undefined;
  const patternGated =
    !solid &&
    cameraZoom !== undefined &&
    shouldUseSolidFallback(bboxAreaWorld(renderable.bounds), cameraZoom);
  const useSolidFallback = patternGated;
  // Gradient WINS over the solid branch (edge case 9): a gradient hatch sets
  // solidFill=1, but the resolved ramp must render, not a flat solid. The
  // gradient path ALWAYS runs when a gradient is present — it is never gated.
  const useGradient = hasGradient;
  const effectiveSolid = !useGradient && (solid || useSolidFallback);

  const tessInput = hatchLoopsToTessellationInput(loops);
  // Pass the RAW-source `hadTaggedExterior` bit (computed pre-drop in
  // `buildHatchSceneGeometry`): `loops` here is the post-walk set with degenerate
  // (<3-vertex) loops already dropped, so a tagged exterior that degenerated is
  // gone and `tessellateHatchLoops`'s own `loops.some(!isHole)` derive would read
  // FALSE and wrongly promote an interior loop to fill (PR #410 P3). The override
  // keeps render, render-bounds, raycast and window-select all gating on the SAME
  // tagged-degenerate signal.
  const tess = tessellateHatchLoops(tessInput, hadTaggedExterior);
  if (tess.indices.length === 0) return null;

  // One truth for "earcut result → indexed BufferGeometry" (shared with the
  // wipeout mask and the wide-polyline band fill) — `./fillMeshGeometry.ts`.
  const geometry = tessellationToBufferGeometry(tess);
  const positions = geometry.getAttribute("position")
    .array as Float32Array;

  let material: THREE.MeshBasicMaterial;
  const color = new THREE.Color(renderable.colorHex);
  let gradientFill = false;
  if (useGradient && gradient !== undefined) {
    material = buildGradientMaterial(geometry, positions, gradient);
    // Flag set true only when the ramp texture actually mapped — a degenerate
    // bbox (or a null jsdom canvas) falls back to a flat color0 fill inside the
    // helper, in which case there is no real gradient to advertise.
    gradientFill = material.map !== null;
  } else if (effectiveSolid) {
    material = new THREE.MeshBasicMaterial({
      color: color.getHex(),
      transparent: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  } else {
    const texture = getOrCreatePatternTexture({
      patternName,
      patternScale,
      patternAngle,
      strokeColorHex: renderable.colorHex,
    });
    if (texture) {
      // UV repeat tied to texture extent. We re-emit a procedural UV
      // attribute (one channel per vertex) using world-X / world-Y so
      // the pattern tiles in world space — RepeatWrapping does the
      // wrapping. Step ≈ 64px per pattern tile; pattern texture is
      // 64×64 so 1 unit of UV ≈ 1 tile worth.
      const uvs = new Float32Array((positions.length / 3) * 2);
      const uvScale = 1 / 64;
      for (let i = 0, j = 0; i < positions.length; i += 3, j += 2) {
        uvs[j] = (positions[i] ?? 0) * uvScale;
        uvs[j + 1] = (positions[i + 1] ?? 0) * uvScale;
      }
      geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
    }
    material = new THREE.MeshBasicMaterial({
      color: color.getHex(),
      transparent: true,
      map: texture ?? null,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  const mesh = new THREE.Mesh(geometry, material);
  mesh.userData = {
    // Codex S9b P2 — attribute the mesh to the parent when this is a synthetic
    // satellite (an MLEADER block-content HATCH child re-homed onto the parent
    // — `renderParentId` present) so the per-viewport visibility mask, keyed on
    // spatial-index membership, keeps it whenever the parent MLEADER is visible.
    // A standalone HATCH has no `renderParentId` and stamps its own id verbatim.
    entityId: renderable.renderParentId ?? renderable.id,
    // Codex PR #784 P1 — the renderable's OWN layer (a satellite child's, not
    // the parent's) + its block-reference layer chain (round 3) for the
    // per-viewport frozen-layer gate (`viewportRenderPass.ts
    // isDirectMeshOnFrozenLayer`).
    ...directMeshLayerStamp(renderable),
    kind: "hatch",
    // PR-V5 — diagnostic flag so test bridges / overlays can see when
    // the solid fallback was applied. Useful for hover tooltip "patterned
    // (rendered solid at this zoom)" UX in a follow-up PR.
    solidFallback: useSolidFallback,
    // Slice 2 — true when the gradient ramp texture rendered (vs a degenerate
    // bbox / gated flat fill). Test bridges + a future hover tooltip read it.
    gradientFill,
  };
  return mesh;
}

/**
 * Slice 2 — build the {@link THREE.MeshBasicMaterial} for a gradient hatch and
 * attach the per-vertex `uv` ramp coordinate to `geometry`.
 *
 *   - The ramp texture is baked / cached by `getOrCreateGradientTexture` (keyed
 *     on colours + symmetric/curved + shadingType, NOT rotation/shift — those
 *     orient the AXIAL ramp here via the UVs). The texture LAYOUT depends on the
 *     shading family (axial 256×1 1-D ramp / radial 256×256 2-D disc).
 *   - AXIAL: each vertex's U is its world-XY projection onto the rotation axis,
 *     mapped across the bbox span biased by `shift` (AutoCAD's "centered" slider)
 *     — the scene analogue of the PDF `axialCoordsPt` projection (batchPaint.ts),
 *     computed directly in scene world units. The U is left UNCLAMPED (P2-B): a
 *     shifted gradient projects some vertices outside [0,1], and clamping
 *     per-vertex would corrupt the affine projection before raster interpolation;
 *     the ramp texture's ClampToEdgeWrapping clamps during SAMPLING instead. V is
 *     fixed at 0.5 (the ramp is 1 texel tall).
 *   - RADIAL: each vertex's UV uses EQUAL world-scale on both axes (P2-A) —
 *     `(0.5 + (x−cx)/(2·r1), 0.5 + (y−cy)/(2·r1))` with `r1 = hypot(w, h)/2` (half
 *     the bbox diagonal, matching the PDF radialCoordsPt) — so the falloff is
 *     CIRCULAR in world space, not stretched into an ellipse on a non-square bbox.
 *     The bbox centre lands at (0.5, 0.5) and the corner at UV-radius 0.5, where
 *     the baked 2-D disc reaches color1; the falloff is read from the disc TEXELS.
 *     A per-vertex `radius/r1` U (an earlier approach) interpolated FLAT across
 *     earcut's boundary-only triangulation — every boundary vertex of a rectangle
 *     is at u≈1, so the centre never reached 0 and the fill rendered flat (P2-1
 *     root cause). Baking the falloff into the texels is affine-correct under ANY
 *     triangulation because it lives in the texels, not the UVs.
 *
 * Material: `color = 0xffffff` (WHITE — the ramp carries the TRUE colours; using
 * `renderable.colorHex` would tint the ramp — edge case 10 trap),
 * `transparent: true`, `depthWrite: false`, `DoubleSide`.
 *
 * Degenerate-bbox guard: a zero-width/height (axial) or zero-radius (radial)
 * bbox would divide by zero — we fall back to a FLAT `color0` fill (no map) so
 * the hatch is still visible (edge case 12). The caller treats `map === null` as
 * "no real gradient rendered".
 */
function buildGradientMaterial(
  geometry: THREE.BufferGeometry,
  positions: Float32Array,
  gradient: NonNullable<
    Extract<RenderableEntity["geometry"], { kind: "hatch" }>["gradient"]
  >,
): THREE.MeshBasicMaterial {
  const box = geometry.boundingBox;
  const color0 = new THREE.Color(gradient.color0Hex).getHex();
  // Defensive: no bounding box (no geometry) → flat color0.
  if (!box) {
    return new THREE.MeshBasicMaterial({
      color: color0,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  const minX = box.min.x;
  const minY = box.min.y;
  const maxX = box.max.x;
  const maxY = box.max.y;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const vertexCount = positions.length / 3;
  const uvs = new Float32Array(vertexCount * 2);
  let degenerate = false;

  if (gradient.shadingType === "radial") {
    // EQUAL-WORLD-SCALE centre-relative UV (P2-A). The radial falloff lives in the
    // baked 2-D disc TEXELS (getOrCreateGradientTexture), NOT in the UVs, so it
    // stays affine-correct under earcut's boundary-only triangulation. To keep the
    // falloff CIRCULAR IN WORLD SPACE (not stretched into an ellipse on a
    // non-square bbox), BOTH axes are scaled by the SAME factor: r1 = half the
    // bbox diagonal = hypot(w, h)/2 (matches the PDF radialCoordsPt,
    // batchPaint.ts:747-760). A vertex at world-distance r1 from the centre (the
    // bbox CORNER) then lands at UV-radius 0.5 from (0.5, 0.5) — where the baked
    // disc reaches color1. Vertices past the corner on a non-square bbox project
    // to UV outside [0,1] and clamp (ClampToEdge) to color1. Normalising X and Y
    // INDEPENDENTLY (the pre-fix planar UV) would map a circular ramp through a
    // rectangular transform → elliptical falloff, differing from the PDF.
    const w = maxX - minX;
    const h = maxY - minY;
    const r1 = Math.hypot(w, h) / 2;
    if (!(r1 > 0)) {
      degenerate = true;
    } else {
      const invDiameter = 1 / (2 * r1);
      for (let i = 0, j = 0; i < positions.length; i += 3, j += 2) {
        uvs[j] = 0.5 + ((positions[i] ?? 0) - cx) * invDiameter;
        uvs[j + 1] = 0.5 + ((positions[i + 1] ?? 0) - cy) * invDiameter;
      }
    }
  } else {
    // AXIAL — project onto the rotation direction; half-span = max |projection|
    // of the four corners (matches PDF axialCoordsPt). `shift` biases the axis
    // midpoint along the direction (centred at 0).
    const dirX = Math.cos(gradient.rotation);
    const dirY = Math.sin(gradient.rotation);
    const corners: Array<[number, number]> = [
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
    ];
    let minProj = Infinity;
    let maxProj = -Infinity;
    for (const [px, py] of corners) {
      const proj = (px - cx) * dirX + (py - cy) * dirY;
      if (proj < minProj) minProj = proj;
      if (proj > maxProj) maxProj = proj;
    }
    const halfSpan = (maxProj - minProj) / 2;
    if (!(halfSpan > 0)) {
      degenerate = true;
    } else {
      const s = Math.max(-1, Math.min(1, gradient.shift)) * halfSpan;
      // Axis runs from (s − halfSpan) → (s + halfSpan) in projection units; map
      // a vertex's projection into U across that span. The U is left UNCLAMPED
      // (P2-B): for a SHIFTED axial gradient some vertices legitimately project
      // outside [0,1], and clamping PER-VERTEX would corrupt the affine projection
      // before raster interpolation (all vertices on one side would collapse to
      // u=0, so triangles interpolate from an artificial endpoint instead of the
      // true projection). The ramp texture is ClampToEdgeWrapping, so an
      // out-of-[0,1] U clamps during SAMPLING — reading the end colour without
      // distorting the interpolation.
      const start = s - halfSpan;
      const span = 2 * halfSpan;
      for (let i = 0, j = 0; i < positions.length; i += 3, j += 2) {
        const proj = ((positions[i] ?? 0) - cx) * dirX + ((positions[i + 1] ?? 0) - cy) * dirY;
        uvs[j] = (proj - start) / span;
        uvs[j + 1] = 0.5;
      }
    }
  }

  if (degenerate) {
    // Edge case 12 — degenerate bbox → flat color0 fill, no map.
    return new THREE.MeshBasicMaterial({
      color: color0,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  const texture = getOrCreateGradientTexture({
    name: gradient.name,
    shadingType: gradient.shadingType,
    color0Hex: gradient.color0Hex,
    color1Hex: gradient.color1Hex,
    symmetric: gradient.symmetric,
    curved: gradient.curved,
  });
  if (!texture) {
    // No canvas (no document + no OffscreenCanvas) → flat color0 fill.
    return new THREE.MeshBasicMaterial({
      color: color0,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  return new THREE.MeshBasicMaterial({
    // WHITE — the ramp texture carries the true colours; tinting by the entity
    // colour would wash the ramp out (edge case 10 trap).
    color: 0xffffff,
    transparent: true,
    map: texture,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/**
 * PR-D — build a `THREE.Mesh` for a WIPEOUT renderable. White fill,
 * opaque, depthWrite=true so it correctly masks underneath geometry.
 */
export function buildWipeoutFillMesh(renderable: RenderableEntity): THREE.Mesh | null {
  if (renderable.geometry.kind !== "wipeout") return null;
  const { corners } = renderable.geometry;
  if (corners.length < 3) return null;

  const tess = tessellateHatchLoops([
    {
      vertices: corners.map((c) => ({ x: c.x, y: c.y, z: c.z })),
      isHole: false,
    },
  ]);
  if (tess.indices.length === 0) return null;

  const geometry = tessellationToBufferGeometry(tess);

  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: false,
    depthWrite: true,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  // Codex S9b P2 — parent-attribute a synthetic MLEADER block-content WIPEOUT
  // child (`renderParentId` present) for the per-viewport visibility mask; a
  // standalone WIPEOUT stamps its own id (same reasoning as the hatch factory).
  mesh.userData = {
    entityId: renderable.renderParentId ?? renderable.id,
    ...directMeshLayerStamp(renderable),
    kind: "wipeout",
  };
  return mesh;
}
